import { Injectable, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AuthService } from '../../core/services/auth/auth.service';
import { PatientFilter } from '../../shared/models/patient-filter';

/** Route prefix the criteria belong to; they are dropped on navigation anywhere else. */
const ACCESS_CONTROLS_ROOT = '/access-controls';

/**
 * Holds the patient search criteria between the Access & Controls landing page (where the dialog
 * collects them) and the results screen (which runs the search).
 *
 * The criteria go through this service rather than through query params on purpose: they are
 * patient identifiers — name, date of birth, MRN — and a URL is the one part of a navigation that
 * reliably ends up in browser history, server logs, and referrer headers. Keeping them in memory
 * costs a bookmarkable results URL, which is why the results screen handles arriving with no
 * criteria (a refresh or a pasted link) by inviting a fresh search instead of erroring.
 */
@Injectable({
  providedIn: 'root',
})
export class PatientAccessSearchStateService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private readonly criteria = signal<PatientFilter | null>(null);

  /** The criteria the results screen should search on, or null when none are pending. */
  readonly activeCriteria = this.criteria.asReadonly();

  constructor() {
    // PHI hygiene: the criteria identify a patient. Don't let them outlive the session that
    // entered them.
    effect(() => {
      if (!this.authService.currentUser()) {
        this.criteria.set(null);
      }
    });

    // Second backstop: these exist only for the hop into the access-management area. Landing
    // anywhere else means they will never be used, so drop them there and then rather than
    // letting patient identifiers sit in a root singleton until sign-out. The service is
    // root-provided and never destroyed, so this subscription lives for the app's lifetime by
    // design.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.clearIfNavigatedAway(event.urlAfterRedirects));
  }

  setCriteria(criteria: PatientFilter): void {
    this.criteria.set(criteria);
  }

  /** Drop the criteria unless the navigation landed inside the access-management area. */
  private clearIfNavigatedAway(url: string): void {
    if (this.criteria() === null) {
      return;
    }

    const path = url.split('?')[0].split('#')[0];
    if (path === ACCESS_CONTROLS_ROOT || path.startsWith(`${ACCESS_CONTROLS_ROOT}/`)) {
      return;
    }

    this.criteria.set(null);
  }
}
